import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useSpring, useTransform } from 'framer-motion';
import {
    Lock, LockOpen,
    TrendingUp, Trash2
} from 'lucide-react';
import { cva } from 'class-variance-authority';
import { toast } from 'sonner';
import * as Tooltip from '@radix-ui/react-tooltip';

import { devLog } from '../../../../../utils/devLogger';
import { createPlaceholderUrl } from '../../../../../utils/PlaceholderCreator';

import SkillsRow_DetailsCell from './cells/SkillsRow_DetailsCell';
import SkillsRow_LevelCell from './cells/SkillsRow_LevelCell';
import SkillScoreBar from './cells/SkillScoreBar';

// CVA: variantes de la celda de costo (P2)
const costCellVariants = cva(
    'py-3 px-2 text-right font-mono text-base font-semibold select-none cursor-pointer relative group/cost transition-colors duration-200',
    {
        variants: {
            state: {
                ignored: 'line-through text-red-500 dark:text-red-400 decoration-red-400 decoration-2',
                active:  'text-emerald-600 dark:text-emerald-400 hover:text-emerald-700',
                inactive:'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400',
            },
        },
        defaultVariants: { state: 'inactive' },
    }
);

// Variantes de animación para la fila completa
const rowVariants = {
    hidden: { opacity: 0, x: -8 },
    visible: (i) => ({
        opacity: 1,
        x: 0,
        transition: { duration: 0.18, ease: 'easeOut', delay: i * 0.03 },
    }),
};

const SkillsPanelDesktopRow = ({
    item,
    skill,
    calculatedCost,
    adjustedScore,
    originalScore,
    multiplier,
    aptitude,
    rank,
    isCalculated,
    isUpgradeActive,
    efficiencyRank,
    isOptimal,
    shouldHighlight,
    showRank,
    onToggleSkill,
    getIconUrl,
    allSkills,
    onToggleFastLearner,
    onLevelChange,
    onToggleIgnoreCost,
    onUpgradeSkill,
    onRemoveSkill,
    rowIndex = 0,
    maxScore = 0,
}) => {
    const { t } = useTranslation();

    devLog('Row Loaded', `Skill ${skill.Id} — "${skill.Skill_name}"`);

    const costState = item.ignoreCost ? 'ignored' : item.active ? 'active' : 'inactive';

    // useSpring para animar el número de score (Crítico 3) ────────────────────
    const springScore = useSpring(adjustedScore || 0, { stiffness: 120, damping: 20 });
    const displayScore = useTransform(springScore, v => Math.round(v));

    React.useEffect(() => {
        springScore.set(adjustedScore || 0);
    }, [adjustedScore, springScore]);

    const handleIgnoreCost = () => {
        if (!onToggleIgnoreCost) return;
        onToggleIgnoreCost(skill.Id);
        if (!item.ignoreCost) {
            toast('Costo ignorado', {
                icon: <Lock size={14} className="text-red-400" />,
                description: skill.Skill_name,
                duration: 2000,
            });
        } else {
            toast('Costo restaurado', {
                icon: <LockOpen size={14} className="text-amber-400" />,
                description: skill.Skill_name,
                duration: 2000,
            });
        }
    };

    const handleRemove = () => {
        // Innate/Awakening: no permitir eliminar
        if (skill.isAuto) {
            const origin = skill.source === 'Innate' ? 'Innate' : 'Awakening';
            toast.error(`Skill permanente (${origin})`, {
                description: 'Las skills automáticas no se pueden eliminar.',
                duration: 3000,
            });
            return;
        }
        onRemoveSkill(skill.Id);
    };

    const handleUpgrade = () => {
        if (onUpgradeSkill) onUpgradeSkill(skill.Id);
    };

    return (
        <motion.tr
            custom={rowIndex}
            initial="hidden"
            animate="visible"
            variants={rowVariants}
            className="group border-b border-gray-100 dark:border-gray-700/50 last:border-0 hover:bg-gray-50/70 dark:hover:bg-gray-700/20 transition-colors duration-200"
        >
            {/* BEST / Rank */}
            <td className="py-3 px-2 text-center w-12">
                {showRank && (
                    <div className={`
                        w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold mx-auto border
                        ${efficiencyRank <= 5
                            ? 'bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-700'
                            : 'bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600'
                        }
                        ${isOptimal ? 'ring-2 ring-emerald-500 ring-offset-1' : ''}
                    `}>
                        {efficiencyRank}
                    </div>
                )}
            </td>

            {/* ACTIVE toggle */}
            <td className="py-3 px-3 text-center w-14">
                <label className="relative inline-flex items-center cursor-pointer">
                    <input
                        type="checkbox"
                        checked={item.active}
                        onChange={() => onToggleSkill(skill.Id)}
                        className="sr-only peer"
                        aria-label={`Activar ${skill.Skill_name}`}
                    />
                    <div className="
                        w-10 h-5 bg-gray-200 dark:bg-gray-600
                        peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-400 peer-focus:ring-offset-1
                        rounded-full peer
                        peer-checked:after:translate-x-full peer-checked:after:border-white
                        after:content-[''] after:absolute after:top-[2px] after:left-[2px]
                        after:bg-white after:border-gray-300 after:border after:rounded-full
                        after:h-4 after:w-4 after:transition-all
                        peer-checked:bg-emerald-500 dark:peer-checked:bg-emerald-500
                    " />
                </label>
            </td>

            {/* DETAILS — Celda extraída (P5 + P7) */}
            <td className="py-3 px-4">
                {/* eslint-disable-next-line react/jsx-pascal-case */}
                <SkillsRow_DetailsCell
                    skill={skill}
                    item={item}
                    getIconUrl={getIconUrl}
                    allSkills={allSkills}
                    shouldHighlight={shouldHighlight}
                />
            </td>

            {/* OPTIONS — Fast Learner */}
            <td className="py-3 px-2 text-center w-28">
                <div className="flex items-center justify-center gap-1.5">
                    <button
                        onClick={() => onToggleFastLearner && onToggleFastLearner(skill.Id)}
                        className={`
                            px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded-full border
                            whitespace-nowrap transition-all duration-150 hover:scale-105 active:scale-95
                            ${item.fastLearner
                                ? 'text-white border-orange-500 shadow-sm shadow-orange-400/30'
                                : 'bg-gray-200 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600'
                            }
                        `}
                        style={item.fastLearner ? { backgroundColor: 'rgb(255, 151, 64)', borderColor: 'rgb(226, 105, 1)' } : {}}
                        aria-label={t('SkillsRow.tooltip_fast_learner')}
                        title={t('SkillsRow.tooltip_fast_learner')}
                    >
                        {t('SkillsRow.label_fast_learner')}
                    </button>
                </div>
            </td>

            {/* LEVEL — Celda extraída (P3 Popover) */}
            <td className="py-3 px-2 text-center w-28">
                {/* eslint-disable-next-line react/jsx-pascal-case */}
                <SkillsRow_LevelCell
                    item={item}
                    skill={skill}
                    onLevelChange={onLevelChange}
                />
            </td>

            {/* COST — con candado P2 + Radix Tooltip (Crítico 1) */}
            <Tooltip.Root delayDuration={600}>
                <Tooltip.Trigger asChild>
                    <td
                        className={costCellVariants({ state: costState })}
                        onClick={handleIgnoreCost}
                    >
                        {/* Candado — ícono lucide-react (P2) */}
                        <span className={`
                            absolute top-1 right-1 transition-all duration-200
                            ${item.ignoreCost
                                ? 'opacity-100 text-badge-red dark:text-red-400'
                                : 'opacity-0 group-hover/cost:opacity-50 text-amber-500'
                            }
                        `}>
                            {item.ignoreCost
                                ? <Lock size={10} />
                                : <LockOpen size={10} />
                            }
                        </span>

                        {calculatedCost > 0
                            ? calculatedCost
                            : <span className="text-gray-300 dark:text-gray-600">—</span>
                        }
                    </td>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                    <Tooltip.Content
                        side="top"
                        sideOffset={6}
                        className="
                            z-50 px-2.5 py-1.5 text-xs font-medium
                            bg-gray-900/95 text-gray-100
                            border border-gray-700
                            rounded-lg shadow-xl backdrop-blur-sm
                        "
                    >
                        {item.ignoreCost
                            ? t('SkillsRow.tooltip_restore_cost')
                            : t('SkillsRow.tooltip_mark_purchased')
                        }
                        <Tooltip.Arrow className="fill-gray-900" />
                    </Tooltip.Content>
                </Tooltip.Portal>
            </Tooltip.Root>

            {/* SCORE — con useSpring animado (Crítico 3) */}
            <td className={`py-3 px-3 text-right w-28 ${item.active ? 'text-gray-700 dark:text-gray-200' : 'text-gray-400 dark:text-gray-600'}`}>
                {isCalculated ? (
                    <div className="flex flex-col items-end gap-0.5">
                        {/* Número animado con useSpring/useTransform */}
                        <motion.span
                            className="font-bold text-base text-emerald-600 dark:text-emerald-400"
                        >
                            {displayScore}
                        </motion.span>
                        {/* D3 score bar — usa token primary-green */}
                        {maxScore > 0 && (
                            <SkillScoreBar
                                score={item.active ? adjustedScore : 0}
                                maxScore={maxScore}
                                color={item.active ? 'rgb(120, 218, 35)' : 'rgb(184, 183, 190)'}
                                width={90}
                            />
                        )}
                        <div className="text-[10px] text-gray-500 dark:text-gray-400 flex flex-col items-end leading-tight">
                            <span>{t('SkillsRow.label_orig')} {originalScore}</span>
                            <span className="text-sky-500 dark:text-sky-400 font-medium">
                                {aptitude} ({rank}): {multiplier}%
                            </span>
                        </div>
                    </div>
                ) : (
                    <span className="font-mono text-base font-semibold">{originalScore || '—'}</span>
                )}
            </td>

            {/* ACTION */}
            <td className="py-3 px-2 text-center w-24">
                <div className="flex items-center justify-center gap-1.5">

                    {/* Upgrade button — Radix Tooltip con Portal (fix scrollbar última fila) */}
                    {skill.upgrade_id && !isUpgradeActive && (() => {
                        const upgradeInfo = allSkills.find(s => parseInt(s.Id_skill, 10) === parseInt(skill.upgrade_id, 10));
                        return (
                            <Tooltip.Root delayDuration={300}>
                                <Tooltip.Trigger asChild>
                                    <button
                                        onClick={handleUpgrade}
                                        className="p-1.5 bg-sky-100 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400 hover:bg-sky-600 hover:text-white dark:hover:bg-sky-500 rounded-lg transition-all duration-150 hover:scale-110 active:scale-95"
                                        aria-label={t('SkillsRow.tooltip_upgrade')}
                                    >
                                        <TrendingUp size={15} />
                                    </button>
                                </Tooltip.Trigger>
                                {upgradeInfo && (
                                    <Tooltip.Portal>
                                        <Tooltip.Content
                                            side="left"
                                            sideOffset={8}
                                            align="center"
                                            avoidCollisions={true}
                                            className="z-50 w-48 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-3 shadow-xl"
                                            style={{ animation: 'fadeIn 0.12s ease-out' }}
                                        >
                                            <div className="flex items-center gap-2">
                                                <div className="w-9 h-9 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600 flex-shrink-0">
                                                    <img
                                                        src={getIconUrl(upgradeInfo.icon_id)}
                                                        className="w-full h-full object-cover"
                                                        alt=""
                                                        onError={(e) => { e.target.src = createPlaceholderUrl(36, 36, '?'); }}
                                                    />
                                                </div>
                                                <div className="text-left min-w-0">
                                                    <div className="text-[9px] text-gray-400 dark:text-gray-500 font-bold uppercase tracking-wider">
                                                        {t('SkillsRow.label_upgrades_to')}
                                                    </div>
                                                    <div className="text-xs font-bold text-gray-800 dark:text-gray-100 leading-tight line-clamp-2">
                                                        {upgradeInfo.Skill_name}
                                                    </div>
                                                </div>
                                            </div>
                                            <Tooltip.Arrow className="fill-white dark:fill-gray-800" width={10} height={5} />
                                        </Tooltip.Content>
                                    </Tooltip.Portal>
                                )}
                            </Tooltip.Root>
                        );
                    })()}

                    {/* Remove button */}
                    <button
                        onClick={handleRemove}
                        className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-150 hover:scale-110 active:scale-95"
                        aria-label={t('SkillsRow.tooltip_remove')}
                    >
                        <Trash2 size={15} />
                    </button>
                </div>
            </td>
        </motion.tr>
    );
};

export default React.memo(SkillsPanelDesktopRow);
// Revisado: 2026-03-27
